/**
 * @fileoverview CAVE API error handling and scan status constants.
 *
 * Defines error normalization, creation, and classification utilities that
 * handle both HTTP API errors and CAVE-specific scan status values. Distinguishes
 * between retriable transient errors and terminal client/server failures.
 */

const parseStatus = value => {
  const status = Number.parseInt(value, 10);
  return Number.isNaN(status) ? undefined : status;
};

const primaryApiError = error => {
  if (Array.isArray(error?.errors) && error.errors.length > 0) {
    return error.errors[0];
  }

  return null;
};

/**
 * Normalizes a CAVE API error response into a consistent shape.
 *
 * Extracts HTTP status, error code, and human-readable detail from various
 * error response formats to provide a uniform interface.
 *
 * @param {Error|object} error - The error object or API response
 * @param {string} [fallbackDetail] - Default detail message if none found
 * @returns {object} Normalized error with { status?, code?, detail }
 */
export const normalizeCaveApiError = (
  error,
  fallbackDetail = 'Document processing request failed.',
) => {
  const primaryError = primaryApiError(error);
  const status = parseStatus(primaryError?.status ?? error?.status);
  const detail =
    primaryError?.detail ||
    primaryError?.title ||
    error?.error ||
    error?.message ||
    fallbackDetail;

  return {
    status,
    code: primaryError?.code,
    detail,
  };
};

/**
 * Creates a CAVE error with optional message prefix and normalized details.
 *
 * Wraps the original error with a new Error instance that includes HTTP status,
 * code, human-readable detail, and a reference to the original error.
 *
 * @param {Error|object} error - The original error or API response
 * @param {object} [opts={}]
 * @param {string} [opts.prefix] - Prefix to prepend to the error message
 * @param {string} [opts.fallbackDetail] - Default detail if none found
 * @returns {Error} Wrapped error with .status, .code, .detail, .originalError
 */
export const createCaveError = (
  error,
  { prefix, fallbackDetail = 'Document processing request failed.' } = {},
) => {
  const normalized = normalizeCaveApiError(error, fallbackDetail);
  const statusSuffix = normalized.status ? ` (${normalized.status})` : '';
  const message = prefix
    ? `${prefix}${statusSuffix}: ${normalized.detail}`
    : normalized.detail;
  const wrappedError = new Error(message);

  wrappedError.status = normalized.status;
  wrappedError.code = normalized.code;
  wrappedError.detail = normalized.detail;
  wrappedError.originalError = error;

  return wrappedError;
};

/**
 * Determines if an error is terminal (not retriable).
 *
 * 4xx client errors (except 408 Request Timeout and 429 Too Many Requests) are
 * terminal and should not trigger retries. Transient errors (5xx, 408, 429) are
 * retriable.
 *
 * @param {Error|object} error - The error or API response
 * @returns {boolean} True if the error is terminal (not retriable)
 */
export const isTerminalCaveApiError = error => {
  const { status } = normalizeCaveApiError(error);

  return status >= 400 && status < 500 && status !== 408 && status !== 429;
};

/**
 * Extracts the user-facing error message from a processing status payload.
 *
 * Searches multiple possible property names and nesting structures to find
 * the failure detail that should be displayed to the user.
 *
 * @param {object} statusPayload - The status response from CAVE
 * @returns {string|null} The error message, or null if not found
 */
export const extractProcessingFailureDetail = statusPayload => {
  const detail =
    statusPayload?.error?.userMessage ||
    statusPayload?.error?.user_message ||
    statusPayload?.error?.errorMessage ||
    statusPayload?.error?.error_message ||
    statusPayload?.error?.detail ||
    statusPayload?.error?.error ||
    statusPayload?.errors?.[0]?.detail;

  return detail?.toString().trim() || null;
};

// The shared CAVE failure contract exposes four scan_status values:
//   pending | completed | completed_with_errors | failed
// `pending` (and any legacy in-progress synonym) is non-terminal; the other
// three are terminal. Anything else is unknown and must fail fast rather than
// poll to the global deadline.
export const CAVE_SCAN_STATUS = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  COMPLETED_WITH_ERRORS: 'completed_with_errors',
  FAILED: 'failed',
};

const TERMINAL_SCAN_STATUSES = new Set([
  CAVE_SCAN_STATUS.COMPLETED,
  CAVE_SCAN_STATUS.COMPLETED_WITH_ERRORS,
  CAVE_SCAN_STATUS.FAILED,
]);

// Legacy/transient in-progress values that should continue polling. `pending`
// is the contract value; `processing` is tolerated for backward compatibility.
const IN_PROGRESS_SCAN_STATUSES = new Set([
  CAVE_SCAN_STATUS.PENDING,
  'processing',
]);

/**
 * Determines if a scan status is terminal (polling should stop).
 * @param {string} scanStatus - The scan status value from CAVE
 * @returns {boolean} True if status is completed, completed_with_errors, or failed
 */
export const isTerminalScanStatus = scanStatus =>
  TERMINAL_SCAN_STATUSES.has(scanStatus);

/**
 * Determines if a scan status indicates active processing (polling should continue).
 * @param {string} scanStatus - The scan status value from CAVE
 * @returns {boolean} True if status is pending or (legacy) processing
 */
export const isInProgressScanStatus = scanStatus =>
  IN_PROGRESS_SCAN_STATUSES.has(scanStatus);

// A scan status we don't recognize at all — neither terminal nor a known
// in-progress value. These must not poll until the deadline.
/**
 * Determines if a scan status is unrecognized (neither terminal nor in-progress).
 *
 * Unrecognized statuses indicate a contract violation and should fail fast
 * rather than continue polling.
 *
 * @param {string} scanStatus - The scan status value from CAVE
 * @returns {boolean} True if status is neither terminal nor in-progress
 */
export const isUnknownScanStatus = scanStatus =>
  !isTerminalScanStatus(scanStatus) && !isInProgressScanStatus(scanStatus);

/**
 * Extracts any processing warnings from a completed_with_errors status payload.
 *
 * Parses warning structures and returns a list of human-readable warning strings.
 *
 * @param {object} statusPayload - The status response from CAVE
 * @returns {Array<string>} Array of warning messages, or empty if none found
 */
export const extractProcessingWarnings = statusPayload => {
  const warnings = statusPayload?.warnings;
  if (!Array.isArray(warnings)) return [];
  return warnings
    .map(w => {
      if (typeof w === 'string') return w.trim();
      const detail =
        w?.userMessage || w?.user_message || w?.message || w?.detail;
      return detail?.toString().trim();
    })
    .filter(Boolean);
};

// An error raised when the artifact summary reports forms were extracted but no
// artifact data could be downloaded, or completes with warnings/partial data.
/**
 * Creates an error for partial processing results (warnings, incomplete data).
 * @param {string} message - The error message
 * @returns {Error} Error with .code = 'partial_result'
 */
export const createCavePartialResultError = message => {
  const error = new Error(message);
  error.code = 'partial_result';
  return error;
};

/**
 * Checks if an error is a partial result error.
 * @param {Error} error - The error to check
 * @returns {boolean} True if error code is 'partial_result'
 */
export const isCavePartialResultError = error =>
  error?.code === 'partial_result';

/**
 * Creates an error for canceled CAVE operations (user abort or deadline exceeded).
 * @param {string} [message] - The error message
 * @returns {Error} Error with .code = 'canceled'
 */
export const createCaveCanceledError = (
  message = 'CAVE processing canceled.',
) => {
  const error = new Error(message);
  error.code = 'canceled';
  return error;
};

/**
 * Checks if an error is a canceled operation error.
 * @param {Error} error - The error to check
 * @returns {boolean} True if error code is 'canceled'
 */
export const isCaveCanceledError = error => error?.code === 'canceled';
